import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { SERVICES } from "../data/content";

const LINKS = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services", dropdown: true },
  { to: "/technologies", label: "Technologies" },
  { to: "/industries", label: "Industries" },
  { to: "/why-us", label: "Why Us" },
  { to: "/process", label: "Process" },
  { to: "/contact", label: "Contact" },
];

const navClass = ({ isActive }) =>
  `px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
    isActive ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
  }`;

export const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return <div className="w-9 h-9" />;
  const isDark = theme === "dark";
  return (
    <button
      data-testid="theme-toggle-button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="w-9 h-9 rounded-full border border-border bg-card/60 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors duration-200"
    >
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dd, setDd] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setDd(false); }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md bg-background/80 border-b border-border/60 shadow-[0_4px_24px_rgb(2,6,23,0.06)]" : "bg-transparent"
      } ${isHome && !scrolled ? "dark" : ""}`}
    >
      <nav className="container-x flex items-center justify-between h-16 sm:h-[72px]">
        <Logo />
        <div className="hidden lg:flex items-center gap-1">
          <NavLink to="/" end className={navClass} data-testid="nav-link-home">Home</NavLink>
          {LINKS.map((l) =>
            l.dropdown ? (
              <div key={l.to} className="relative" onMouseEnter={() => setDd(true)} onMouseLeave={() => setDd(false)}>
                <NavLink to={l.to} className={navClass} data-testid="nav-link-services">
                  <span className="inline-flex items-center gap-1">Services <ChevronDown size={14} className={`transition-transform duration-200 ${dd ? "rotate-180" : ""}`} /></span>
                </NavLink>
                <AnimatePresence>
                  {dd && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.18 }}
                      data-testid="services-mega-menu"
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[560px]"
                    >
                      <div className="glass-card rounded-2xl p-5 grid grid-cols-2 gap-1 bg-popover/95">
                        {SERVICES.map((s) => (
                          <Link key={s.id} to="/services" data-testid={`mega-menu-${s.id}`}
                            className="group flex items-start gap-2.5 rounded-xl px-3 py-2.5 hover:bg-primary/10 transition-colors duration-200">
                            <ArrowRight size={14} className="mt-1 text-primary opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                            <span>
                              <span className="block text-sm font-semibold">{s.title}</span>
                              <span className="block text-xs text-muted-foreground line-clamp-1">{s.desc}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink key={l.to} to={l.to} className={navClass} data-testid={`nav-link-${l.label.toLowerCase()}`}>{l.label}</NavLink>
            )
          )}
        </div>
        <div className="flex items-center gap-2.5">
          <ThemeToggle />
          <Link to="/contact" data-testid="nav-get-started-btn"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 px-5 py-2 text-sm font-semibold text-white shadow-[0_4px_20px_rgba(99,102,241,0.4)] hover:shadow-[0_6px_28px_rgba(99,102,241,0.55)] hover:-translate-y-0.5 transition-all duration-200">
            Get Started <ArrowRight size={15} />
          </Link>
          <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} aria-label="Toggle menu"
            className="lg:hidden w-9 h-9 rounded-lg border border-border bg-card/60 flex items-center justify-center text-foreground">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-background/95 border-b border-border"
          >
            <div className="container-x py-4 flex flex-col gap-1">
              <NavLink to="/" end className={navClass} data-testid="mobile-nav-home">Home</NavLink>
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className={navClass} data-testid={`mobile-nav-${l.label.toLowerCase()}`}>{l.label}</NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-get-started-btn"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 px-5 py-2.5 text-sm font-semibold text-white">
                Get Started <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
